'use client';

import { useStoreMap } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { ListItemIcon } from '@nx-frontend/shared/ui/list-item';
import { getUploadFileByIdParams } from '@nx-frontend/shared/store/feature-files';
import { getTextClassNames } from '@nx-frontend/shared/themes';
import {
  FileType,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
  UploadFileStatus,
} from '@nx-frontend/shared/types';
import { getIconByFileType } from '../helpers';

interface FileProgressListItemProps {
  id: string;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
}

export const FileProgressListItem: React.FC<FileProgressListItemProps> = ({ id, themeColors, themeStyle }) => {
  const item = useStoreMap(getUploadFileByIdParams(id));

  if (!item) {
    return;
  }

  const handleUploadCancel = () => {
    if (item.canceler) {
      item.canceler();
    }
  };
  const icon = getIconByFileType(FileType.FILE);
  const percents = Math.round((100 / item.size) * item.uploadedSize);

  return (
    <div className={classNames('flex items-center justify-between w-full py-2.5 sm:px-4 text-[13px] font-normal')}>
      <div className="flex items-center justify-start gap-3 relative max-w-fill min-w-0">
        <ListItemIcon themeColors={themeColors} themeStyle={themeStyle} icon={icon} />
        <div className="flex flex-col font-normal items-start overflow-hidden min-w-0">
          <div
            className={classNames(
              'pr-2 text-[13px] overflow-hidden overflow-ellipsis whitespace-nowrap max-w-full',
              getTextClassNames(themeColors, PortalTextType.SURFACE)
            )}
          >
            {item.title}
          </div>
        </div>
      </div>

      {item.status === UploadFileStatus.IN_PROGRESS && (
        <div className={classNames('flex items-center', getTextClassNames(themeColors, PortalTextType.PRIMARY))}>
          <div className="mr-1 flex w-7">
            <span>{percents}</span>
            <span>%</span>
          </div>
          <div className="mr-1">
            <div className="animate-spin">
              <Icon
                icon={IconsMap.LOADER_1}
                className={classNames('w-6 h-6', getTextClassNames(themeColors, PortalTextType.PRIMARY))}
              />
            </div>
          </div>
          <div className="cursor-pointer" onClick={handleUploadCancel}>
            <Icon icon={IconsMap.CLOSE} className="w-4 h-4" />
          </div>
        </div>
      )}
      {item.status === UploadFileStatus.CANCELED && (
        <div
          className={classNames('text-right whitespace-nowrap', getTextClassNames(themeColors, PortalTextType.PRIMARY))}
        >
          Uploading canceled
        </div>
      )}
      {item.status === UploadFileStatus.ERROR && <div className="text-neutral-500">Error</div>}
      {item.status === UploadFileStatus.DONE && (
        <div>
          <div className="w-[14px] h-[14px] rounded-full bg-green-500 flex items-center justify-center">
            <Icon icon={IconsMap.CHECK_BULLET} className="w-3 h-3 text-white" />
          </div>
        </div>
      )}
    </div>
  );
};
